import { useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { CohortBanner } from '@/components/cohort-banner';
import { LongPressHint } from '@/components/long-press-hint';
import { PersonCard } from '@/components/person-card';
import { PersonRow } from '@/components/person-row';
import { RoleSheet } from '@/components/role-sheet';
import { RosterStrip } from '@/components/roster-strip';
import { spinTotal } from '@/components/slot-reel';
import { COHORTS, getCohort } from '@/game/cohorts';
import { useGame } from '@/game/game-context';
import { markLongPressHintSeen, shouldShowLongPressHint } from '@/game/hints';
import { ALL_PEOPLE } from '@/game/people';
import { createRng } from '@/game/rng';
import { TOTAL_ROUNDS, cohortLineup, isComplete, openRoles } from '@/game/session';
import type { Cohort, Person, Role } from '@/game/types';
import { MAX_WIDTH, colors, radius, type } from '@/theme';

const INTERSTITIAL_MS = 760;

/** How many faces each reel rushes past before it lands. */
const REEL_DECOYS = 9;
/**
 * A round advance hides the board behind the interstitial, so the reels wait
 * for it to lift — otherwise they'd finish spinning in the dark. A reroll has
 * no interstitial and spins straight away.
 */
const REEL_WAIT_MS = 620;
/** The banner lands first, then the rows, top to bottom. */
const ROWS_OFFSET = 160;
const ROW_STAGGER = 60;

/** The board as it should be drawn — a live draw, or the frozen final one. */
type Board = { cohort: Cohort; lineup: Person[]; drawIndex: number };

export default function DraftScreen() {
  const router = useRouter();
  const { state, draft, reroll } = useGame();

  /** The person whose "who is this?" card is open, held by a long press. */
  const [profile, setProfile] = useState<Person | null>(null);
  /** Null until the stored flag has been read — the hint can't decide before then. */
  const [hintOwed, setHintOwed] = useState<boolean | null>(null);
  const [hintVisible, setHintVisible] = useState(false);

  const [pending, setPending] = useState<Person | null>(null);
  const [justFilled, setJustFilled] = useState<Role | null>(null);
  const [interstitial, setInterstitial] = useState<number | null>(null);
  const [reelWait, setReelWait] = useState(0);
  /**
   * The last pick still redraws the board underneath "TEAM LOCKED". Pinning it
   * to what the player was looking at keeps that beat from flashing a reshuffle.
   */
  const [frozenBoard, setFrozenBoard] = useState<Board | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const scroller = useRef<ScrollView>(null);

  const cohort = getCohort(state.cohortId);
  const lineup = useMemo(() => cohortLineup(state), [state]);
  const remaining = openRoles(state);
  const complete = isComplete(state);
  const board = complete && frozenBoard ? frozenBoard : { cohort, lineup, drawIndex: state.drawIndex };

  /** Reel filler. Seeded like everything else so a run stays reproducible. */
  const decoyCohorts = useMemo(() => {
    const rng = createRng(`${state.seed}:reel-cohort:${state.drawIndex}`);
    return rng.shuffle(COHORTS.filter((c) => c.id !== state.cohortId)).slice(0, REEL_DECOYS);
  }, [state.seed, state.drawIndex, state.cohortId]);

  const decoyPeople = useMemo(() => {
    const rng = createRng(`${state.seed}:reel-people:${state.drawIndex}`);
    const onBoard = new Set(lineup.map((person) => person.id));
    const pool = rng.shuffle(ALL_PEOPLE.filter((person) => !onBoard.has(person.id)));
    return lineup.map((_, row) =>
      Array.from({ length: REEL_DECOYS }, (_, i) => pool[(row * REEL_DECOYS + i) % pool.length]),
    );
  }, [state.seed, state.drawIndex, lineup]);

  const after = useCallback((ms: number, fn: () => void) => {
    timers.current.push(setTimeout(fn, ms));
  }, []);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    if (!complete) return;
    const timer = setTimeout(() => router.replace('/result'), 900);
    return () => clearTimeout(timer);
  }, [complete, router]);

  /**
   * A new round is a new board, so it starts at the top. The interstitial is
   * already covering the screen when this lands, so the jump is never seen.
   */
  useEffect(() => {
    if (complete) return;
    scroller.current?.scrollTo({ y: 0, animated: false });
  }, [state.round, complete]);

  useEffect(() => {
    let live = true;
    shouldShowLongPressHint().then((owed) => {
      if (live) setHintOwed(owed);
    });
    return () => {
      live = false;
    };
  }, []);

  /**
   * The one-time hint, held until the reels have landed — a tooltip that fires
   * mid-spin reads as noise on a board that's still moving. The flag is written
   * as it goes up rather than when it's scheduled, so a crash in between leaves
   * the one showing intact.
   */
  useEffect(() => {
    if (!hintOwed || complete || state.round !== 1) return;
    const timer = setTimeout(() => {
      setHintVisible(true);
      markLongPressHintSeen();
    }, spinTotal(ROWS_OFFSET) + 250);
    return () => clearTimeout(timer);
  }, [hintOwed, complete, state.round]);

  const dismissHint = useCallback(() => {
    setHintVisible(false);
    setHintOwed(false);
  }, []);

  const onLongPress = (person: Person) => {
    dismissHint();
    setProfile(person);
  };

  const onAssign = (role: Role) => {
    if (!pending) return;
    const nextRound = state.round + 1;
    if (nextRound > TOTAL_ROUNDS) setFrozenBoard({ cohort, lineup, drawIndex: state.drawIndex });
    draft(pending.id, role);
    setPending(null);
    setJustFilled(role);
    after(1400, () => setJustFilled(null));
    setReelWait(nextRound <= TOTAL_ROUNDS ? REEL_WAIT_MS : 0);
    if (nextRound <= TOTAL_ROUNDS) {
      setInterstitial(nextRound);
      after(INTERSTITIAL_MS, () => setInterstitial(null));
    }
  };

  const onReroll = () => {
    setReelWait(0);
    reroll();
  };

  return (
    <SafeAreaView style={styles.safe}>
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.heading}>Build your startup</Text>
          <View style={styles.headerRight}>
            <View style={[styles.rerollPill, state.rerollsLeft === 0 && styles.rerollPillEmpty]}>
              <Text style={[styles.rerollPillText, state.rerollsLeft === 0 && styles.rerollPillTextEmpty]}>
                ⟳ {state.rerollsLeft}
              </Text>
            </View>
            <Text style={styles.round}>
              Round {Math.min(state.round, TOTAL_ROUNDS)} / {TOTAL_ROUNDS}
            </Text>
          </View>
        </View>

        <ScrollView
          ref={scroller}
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}>
          {/* The last assign leaves the board frozen on its way to /result.
              Spinning a reel we're about to navigate away from reads as a glitch. */}
          <CohortBanner
            key={`banner-${board.drawIndex}`}
            cohort={board.cohort}
            decoys={complete ? undefined : decoyCohorts}
            spinDelay={reelWait}
          />

          <View key={`lineup-${board.drawIndex}`} style={styles.lineup}>
            {board.lineup.map((person, index) => (
              // The hint hangs out of this wrapper onto the row below, so the
              // wrapper has to win the paint order while it's up.
              <View
                key={person.id}
                style={[styles.rowWrap, hintVisible && index === 0 && styles.rowWrapAboveHint]}>
                <PersonRow
                  person={person}
                  index={index}
                  selected={pending?.id === person.id}
                  onPress={() => setPending(person)}
                  onLongPress={() => onLongPress(person)}
                  decoys={complete ? undefined : decoyPeople[index]}
                  spinDelay={reelWait + ROWS_OFFSET + index * ROW_STAGGER}
                />
                {hintVisible && index === 0 && <LongPressHint onDismiss={dismissHint} />}
              </View>
            ))}
          </View>
        </ScrollView>

        <Pressable
          onPress={onReroll}
          disabled={state.rerollsLeft === 0 || complete}
          style={({ pressed }) => [
            styles.reroll,
            state.rerollsLeft === 0 && styles.rerollDisabled,
            pressed && styles.rerollPressed,
          ]}>
          <Text style={[styles.rerollText, state.rerollsLeft === 0 && styles.rerollTextDisabled]}>
            {state.rerollsLeft > 0 ? `Reroll company · ${state.rerollsLeft} left` : 'No rerolls left'}
          </Text>
        </Pressable>

        <View style={styles.rosterWrap}>
          <RosterStrip roster={state.roster} highlight={justFilled} />
        </View>
      </View>

      {profile && !complete && (
        <PersonCard person={profile} cohort={board.cohort} onClose={() => setProfile(null)} />
      )}

      {pending && !complete && (
        <RoleSheet
          person={pending}
          roles={remaining}
          onAssign={onAssign}
          onCancel={() => setPending(null)}
        />
      )}

      {interstitial !== null && (
        // No entering animation on purpose: the board behind it remounts in this
        // same commit, so anything short of instant cover shows the redraw.
        <Animated.View
          exiting={FadeOut.duration(220)}
          pointerEvents="none"
          style={styles.interstitial}>
          <Text style={styles.interstitialRound}>ROUND {interstitial}</Text>
          <Text style={styles.interstitialSub}>OF {TOTAL_ROUNDS}</Text>
        </Animated.View>
      )}

      {complete && (
        <Animated.View entering={FadeIn.duration(400)} pointerEvents="none" style={styles.interstitial}>
          <Text style={styles.interstitialRound}>TEAM LOCKED</Text>
          <Text style={styles.interstitialSub}>SIMULATING</Text>
        </Animated.View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  container: {
    flex: 1,
    width: '100%',
    maxWidth: MAX_WIDTH,
    alignSelf: 'center',
    paddingHorizontal: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 8,
    paddingBottom: 16,
    gap: 12,
  },
  heading: {
    ...type.title,
    fontSize: 26,
    color: colors.text,
    flexShrink: 1,
  },
  headerRight: {
    alignItems: 'flex-end',
    gap: 5,
  },
  rerollPill: {
    backgroundColor: 'rgba(242, 203, 78, 0.14)',
    borderRadius: radius.pill,
    paddingHorizontal: 10,
    paddingVertical: 3,
  },
  rerollPillEmpty: {
    backgroundColor: colors.cardHi,
  },
  rerollPillText: {
    ...type.label,
    fontSize: 11,
    letterSpacing: 0.5,
    color: colors.gold,
  },
  rerollPillTextEmpty: {
    color: colors.faint,
  },
  round: {
    ...type.body,
    fontSize: 14,
    color: colors.dim,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    gap: 12,
    paddingBottom: 4,
  },
  lineup: {
    gap: 9,
  },
  rowWrap: {
    gap: 0,
  },
  rowWrapAboveHint: {
    zIndex: 10,
  },
  reroll: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'dashed',
    borderRadius: radius.lg,
    paddingVertical: 14,
    alignItems: 'center',
  },
  rerollDisabled: {
    opacity: 0.45,
  },
  rerollPressed: {
    backgroundColor: colors.card,
  },
  rerollText: {
    ...type.body,
    fontSize: 15,
    color: colors.dim,
  },
  rerollTextDisabled: {
    color: colors.faint,
  },
  rosterWrap: {
    paddingTop: 12,
    paddingBottom: 8,
    borderTopWidth: 1,
    borderTopColor: colors.borderSoft,
  },
  interstitial: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.bg,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  interstitialRound: {
    ...type.display,
    fontSize: 54,
    color: colors.text,
    letterSpacing: -2,
  },
  interstitialSub: {
    ...type.label,
    fontSize: 12,
    color: colors.gold,
  },
});
